import { useCallback } from 'react';
import { useUpdateEffect } from 'react-use';

import useEvent from '@lyearn/core/hooks/useEvent';
import { setIn } from '@lyearn/core/utils/immutable';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useListWithTempId from '@/hooks/useList/useListWithTempId';
import { reorderItems } from '@/modules/corporate/components/DraggableList/utils';
import { QuestionEditorProps } from '@/modules/corporate/modules/questions/fields/types';
import { getIsCorrectAnswerChanged } from '@/modules/corporate/modules/quiz/views/QuizEditor/utils/questions';
import { OptionInput } from '@/types/schema';

import { onQuizOptionsAction, QuizOptionsActionEnum } from './actions';
import { getEmptyOption } from './utils';

const EMPTY_ARRAY: never[] = [];

const useOptionsAction = ({
  question,
  onChange,
  isMultiSelect,
  variant = 'quiz',
}: Pick<QuestionEditorProps, 'question' | 'onChange' | 'isMultiSelect'> & {
  variant?: 'quiz' | 'survey';
}) => {
  const { t } = useTranslation();
  const { showErrorSnackbar } = useSnackbar();

  const onListItemsChange = useCallback(
    (nextOptions: OptionInput[]) => {
      const options = nextOptions.map(({ tempId, ...option }: any) => option);
      onChange(setIn(question, 'options.set', options));
    },
    [onChange, question],
  );

  const [
    options,
    {
      push,
      onDelete,
      onListInputChange,
      setListItems,
      pushMultiple,
      initializeItems,
      setListWithTempId,
    },
  ] = useListWithTempId(question.options?.set || EMPTY_ARRAY, onListItemsChange);

  useUpdateEffect(() => {
    const nextOptions = options.map((option) => ({ ...option, optionEvaluation: false }));
    setListItems(nextOptions);
  }, [question.questionType]);

  useUpdateEffect(() => {
    const questionOptions = question.options?.set || EMPTY_ARRAY;
    // this is for the case when we remove empty options during validation
    if (questionOptions.length < options.length) {
      initializeItems(questionOptions);
    }
  }, [question.options?.set.length]);

  useUpdateEffect(
    () => {
      const questionOptions = question.options?.set || EMPTY_ARRAY;
      if (
        questionOptions.length === options.length &&
        getIsCorrectAnswerChanged(questionOptions, options)
      ) {
        setListWithTempId(
          options.map((option, index) => ({
            ...option,
            optionEvaluation: questionOptions[index].optionEvaluation,
          })),
        );
      }
    },
    question.options?.set,
  );

  const onAction: onQuizOptionsAction = useEvent((action) => {
    switch (action.type) {
      case QuizOptionsActionEnum.AddOption: {
        push(getEmptyOption());
        break;
      }

      case QuizOptionsActionEnum.AddOptions: {
        const { newOptions } = action.payload;
        pushMultiple(newOptions);
        break;
      }

      case QuizOptionsActionEnum.UpdateOption: {
        const { index, option } = action.payload;
        onListInputChange(option, index);
        break;
      }

      case QuizOptionsActionEnum.RemoveOption: {
        if (variant === 'quiz' && options.length <= 2) {
          showErrorSnackbar({ message: t('QuizCreator:Validations.OptionLengthError') });
          return;
        }
        onDelete(action.payload.index);
        break;
      }

      case QuizOptionsActionEnum.ReorderOption: {
        const { fromIndex, toIndex } = action.payload;
        setListItems(reorderItems({ fromIndex, toIndex, items: options }));
        break;
      }

      case QuizOptionsActionEnum.SetOptionEvaluation: {
        const { index, optionEvaluation } = action.payload;

        const nextOptions =
          !isMultiSelect && optionEvaluation
            ? options.map((option) => ({ ...option, optionEvaluation: false }))
            : options;

        setListItems(setIn(nextOptions, [index, 'optionEvaluation'], optionEvaluation));
        break;
      }

      default: {
        break;
      }
    }
  });

  return { options, onAction };
};

export default useOptionsAction;
